import { action, makeObservable, observable, runInAction } from 'mobx';

export type JourneyStage = 'SHOPPING' | 'PLANNING' | 'DISCOVERY';

export type MostRecentSearch = {
	checkin: string;
	checkout: string;
	rooms: { adults: number; children: number }[];
};

/**
 * Per-property journey state as resolved by `window.FS.DECISION_ENGINE.getState`.
 * Every field is optional — an unknown visitor resolves to `{}`.
 */
export type JourneyState = {
	journeyStage?: JourneyStage;
	mostRecentSearch?: MostRecentSearch;
	mostRecentlyViewed?: { section?: string; url?: string; timestamp?: string };
};

type DecisionEngineApi = {
	getState: (propertyCode?: string) => Promise<JourneyState | undefined> | JourneyState | undefined;
	getCookie: () => string | undefined;
	recordView: (opts: { property: string; section?: string }) => Promise<void>;
	recordSearch: (opts: {
		property: string;
		checkin: string;
		checkout: string;
		promo?: string;
		rooms?: { bed_code: string; offer_code: string; adults: number; children: number }[];
	}) => Promise<void>;
	recordReservation: (opts: { property: string }) => Promise<void>;
};

/**
 * `window` event the host page's decision-engine loader dispatches immediately
 * after it assigns `window.FS.DECISION_ENGINE`. The engine script exposes no
 * other readiness signal (no callback, no ready flag, no pre-ready call queue),
 * so this store either finds the API already on `window` at construction time or
 * waits for this event — it never polls.
 *
 * The loader is expected to do, right after assigning the API:
 *
 *   window.FS.DECISION_ENGINE = { ... };
 *   window.dispatchEvent(new Event('fs:decision-engine-ready'));
 */
export const DECISION_ENGINE_READY_EVENT = 'fs:decision-engine-ready';

const readDecisionEngine = (): DecisionEngineApi | undefined =>
	typeof window !== 'undefined'
		? (window.FS?.['DECISION_ENGINE'] as DecisionEngineApi | undefined)
		: undefined;

/**
 * Bridges the page-global `window.FS.DECISION_ENGINE` API into MobX so
 * `observer` components pick up the journey state whenever the engine script
 * finishes loading — which, on a real page, happens after React has already
 * rendered. Consumers read {@link JourneyStore.getStage} /
 * {@link JourneyStore.getMostRecentSearch} and fall back to the discovery phase
 * until `getState` resolves.
 *
 * Exported as the {@link journeyStore} singleton: the decision engine is itself
 * one page-global instance, so there is nothing per-mount to own.
 */
class JourneyStore {
	/** True once `window.FS.DECISION_ENGINE` has been found. */
	ready = false;

	private api: DecisionEngineApi | null = null;
	private stateByProperty = new Map<string, JourneyState>();
	/** Property codes a consumer has asked about; replayed once the engine is ready. */
	private tracked = new Set<string>();
	/** Bumped by reset() so an in-flight getState() from a prior run is discarded. */
	private generation = 0;

	constructor() {
		makeObservable<JourneyStore, 'api' | 'stateByProperty' | 'bindApi' | 'handleReady'>(this, {
			ready: observable,
			api: observable.ref,
			stateByProperty: observable,
			track: action,
			handleReady: action.bound,
			bindApi: action,
			reset: action,
		});

		this.listen();
	}

	private listen() {
		const existing = readDecisionEngine();
		if (existing) {
			this.bindApi(existing);
		} else if (typeof window !== 'undefined') {
			window.addEventListener(DECISION_ENGINE_READY_EVENT, this.handleReady);
		}
	}

	private handleReady() {
		const api = readDecisionEngine();
		// The event can only fire in the browser, and only meaningfully once the
		// API is actually assigned — ignore a premature dispatch and keep waiting.
		if (!api) return;
		window.removeEventListener(DECISION_ENGINE_READY_EVENT, this.handleReady);
		this.bindApi(api);
	}

	private bindApi(api: DecisionEngineApi) {
		this.api = api;
		this.ready = true;
		this.tracked.forEach(code => void this.loadState(code));
	}

	/**
	 * Register interest in a property's journey state. Safe to call before the
	 * engine is ready and safe to call repeatedly — `getState` runs at most once
	 * per property. Call this from an effect, never during render.
	 */
	track(propertyCode: string) {
		if (!propertyCode || this.tracked.has(propertyCode)) return;
		this.tracked.add(propertyCode);
		if (this.api) void this.loadState(propertyCode);
	}

	private async loadState(propertyCode: string) {
		const api = this.api;
		if (!api) return;
		const generation = this.generation;
		try {
			const state = await api.getState(propertyCode);
			// A reset() or API swap happened while awaiting — the result is stale.
			if (this.generation !== generation || this.api !== api) return;
			if (state) {
				runInAction(() => this.stateByProperty.set(propertyCode, state));
			}
		} catch {
			// Leave the property on its default (discovery) phase.
		}
	}

	/**
	 * Observable read. `undefined` until the engine is ready *and* `getState` has
	 * resolved for this property — callers fall back to discovery in that window.
	 */
	getStage(propertyCode: string): JourneyStage | undefined {
		return this.stateByProperty.get(propertyCode)?.journeyStage;
	}

	/** The visitor's last search for this property, once `getState` has resolved. */
	getMostRecentSearch(propertyCode: string): MostRecentSearch | undefined {
		return this.stateByProperty.get(propertyCode)?.mostRecentSearch;
	}

	/** Test-only: forget resolved state and re-arm the readiness listener. */
	reset() {
		if (typeof window !== 'undefined') {
			window.removeEventListener(DECISION_ENGINE_READY_EVENT, this.handleReady);
		}
		this.generation++;
		this.api = null;
		this.ready = false;
		this.stateByProperty.clear();
		this.tracked.clear();
		this.listen();
	}
}

export const journeyStore = new JourneyStore();
export { JourneyStore };
